import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { enquiryMailto } from "@/lib/enquiry";
import { ENQUIRY_EMAIL } from "@/lib/brand";

export const Route = createFileRoute("/legal/paia")({
  component: PaiaPage,
});

function PaiaPage() {
  return (
    <MarketingLayout>
      <Helmet>
        <title>PAIA Manual | Larasoft</title>
        <meta
          name="description"
          content="Access Larasoft's Promotion of Access to Information (PAIA) manual and compliance resources."
        />
        <link rel="canonical" href="https://larasoft.global/legal/paia" />
      </Helmet>
      <Section className="py-16">
        <div className="mx-auto max-w-4xl">
          <h1 className="mb-8 text-4xl font-bold">PAIA Manual</h1>

          <div className="prose prose-slate max-w-none">
            <p className="lead text-lg text-slate-700">
              Promotion of Access to Information Act (PAIA) Manual for Larasoft
              (Pty) Ltd
            </p>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">About PAIA</h2>
              <p>
                The Promotion of Access to Information Act 2 of 2000 (PAIA)
                gives effect to the constitutional right of access to
                information held by public and private bodies. This manual is
                compiled in accordance with Section 51 of PAIA.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                Company Information
              </h2>
              <div className="rounded-lg bg-slate-50 p-6">
                <p className="mb-2">
                  <strong>Company Name:</strong> Larasoft (Pty) Ltd
                </p>
                <p className="mb-2">
                  <strong>Contact Email:</strong>{" "}
                  <a
                    href={enquiryMailto("/legal/paia")}
                    className="text-blue-600 underline"
                  >
                    {ENQUIRY_EMAIL}
                  </a>
                </p>
                <p className="mb-2">
                  <strong>Phone:</strong> +27 82 457 8390
                </p>
                <p>
                  <strong>Website:</strong>{" "}
                  <a
                    href="https://larasoft.global"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    larasoft.global
                  </a>
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                Information Officer
              </h2>
              <p>
                The Information Officer is responsible for PAIA and POPIA
                compliance:
              </p>
              <div className="mt-4 rounded-lg bg-slate-50 p-6">
                <p className="mb-2">
                  <strong>Contact:</strong> {ENQUIRY_EMAIL}
                </p>
                <p>
                  <strong>Phone:</strong> +27 82 457 8390
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                Purpose of This Manual
              </h2>
              <p>This PAIA Manual provides information about:</p>
              <ul className="mt-4 space-y-2">
                <li>The types of records held by Larasoft</li>
                <li>How to request access to records</li>
                <li>The process and timelines for handling requests</li>
                <li>Fees applicable to information requests</li>
                <li>Grounds for refusal of access</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                Records Available
              </h2>
              <p>Larasoft maintains the following categories of records:</p>
              <ul className="mt-4 space-y-2">
                <li>Company registration and statutory documents</li>
                <li>Client contracts and service agreements</li>
                <li>Financial records and accounting information</li>
                <li>
                  Data processing records (see our{" "}
                  <Link
                    to="/legal/data-processing"
                    className="text-blue-600 underline"
                  >
                    Data Processing Register
                  </Link>
                  )
                </li>
                <li>Privacy and compliance policies</li>
                <li>Technical documentation and system records</li>
                <li>Employment and HR records</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                How to Request Access
              </h2>
              <p>To request access to records under PAIA:</p>
              <ol className="mt-4 space-y-3">
                <li>
                  <strong>Submit a written request</strong> to our Information
                  Officer at{" "}
                  <a
                    href={enquiryMailto("/legal/paia")}
                    className="text-blue-600 underline"
                  >
                    {ENQUIRY_EMAIL}
                  </a>
                </li>
                <li>
                  <strong>Provide sufficient detail</strong> about the record(s)
                  you&apos;re requesting
                </li>
                <li>
                  <strong>Specify</strong> your preferred form of access
                  (inspection, copy, etc.)
                </li>
                <li>
                  <strong>Include</strong> your contact details for communication
                </li>
                <li>
                  <strong>Pay applicable fees</strong> (if any) once the request
                  is processed
                </li>
              </ol>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                Processing Timeline
              </h2>
              <p>
                We will respond to your request within{" "}
                <strong>30 days</strong> of receipt. If we need to extend this
                period, we will notify you in writing with reasons for the
                extension.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">Fees</h2>
              <p>PAIA provides for fees to be charged for:</p>
              <ul className="mt-4 space-y-2">
                <li>Request processing</li>
                <li>Searching and preparing records</li>
                <li>Photocopying and printing</li>
                <li>Postage (if applicable)</li>
              </ul>
              <p className="mt-4">
                Fees will be calculated according to the rates prescribed in the
                PAIA regulations and communicated to you before processing your
                request.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                Grounds for Refusal
              </h2>
              <p>
                Access to records may be refused on the following grounds:
              </p>
              <ul className="mt-4 space-y-2">
                <li>
                  Protection of personal information of third parties
                </li>
                <li>Commercial confidentiality</li>
                <li>Protection of legally privileged information</li>
                <li>
                  Protection of safety of individuals or property
                </li>
                <li>Other grounds specified in PAIA</li>
              </ul>
              <p className="mt-4">
                If we refuse your request, we will provide written reasons and
                inform you of your right to appeal.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                Related Resources
              </h2>
              <div className="mt-4 space-y-2">
                <p>
                  <Link
                    to="/legal/privacy"
                    className="text-blue-600 underline"
                  >
                    Privacy Notice
                  </Link>{" "}
                  - Learn how we protect your personal information under POPIA
                </p>
                <p>
                  <Link
                    to="/legal/data-processing"
                    className="text-blue-600 underline"
                  >
                    Data Processing Register
                  </Link>{" "}
                  - View our complete record of processing activities
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">Contact Us</h2>
              <p>
                For PAIA requests or questions about this manual, please
                contact:
              </p>
              <div className="mt-4 rounded-lg bg-slate-50 p-4">
                <p className="mb-2">
                  <strong>Information Officer</strong>
                </p>
                <p className="mb-2">
                  <strong>Email:</strong>{" "}
                  <a
                    href={enquiryMailto("/legal/paia")}
                    className="text-blue-600 underline"
                  >
                    {ENQUIRY_EMAIL}
                  </a>
                </p>
                <p>
                  <strong>Phone:</strong> +27 82 457 8390
                </p>
              </div>
            </section>
          </div>
        </div>
      </Section>
    </MarketingLayout>
  );
}
